import React, { createContext, useContext, useReducer, useEffect } from 'react';
import type { ReactNode } from 'react';
import { AuthService } from '@/services/auth';
import type { User, LoginCredentials, AuthState } from '@/types';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials, signal?: AbortSignal) => Promise<{
    success: boolean;
    error?: string;
    requires_2fa?: boolean;
    temp_token?: string;
  }>;
  verifyTwoFactor: (temp_token: string, code: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'SET_USER'; payload: User }
  | { type: 'SET_LOADING'; payload: boolean };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true };
    case 'LOGIN_SUCCESS':
      return { user: action.payload.user, token: action.payload.token, isAuthenticated: true, isLoading: false };
    case 'LOGIN_FAILURE':
      return { user: null, token: null, isAuthenticated: false, isLoading: false };
    case 'LOGOUT':
      return { user: null, token: null, isAuthenticated: false, isLoading: false };
    case 'SET_USER':
      return { ...state, user: action.payload, isAuthenticated: true };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    default:
      return state;
  }
};

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const token = AuthService.getToken();
    const user = AuthService.getUser();
    if (token && user && AuthService.isAuthenticated()) {
      dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } });
    } else {
      AuthService.clearAuth();
      dispatch({ type: 'LOGOUT' });
    }
    dispatch({ type: 'SET_LOADING', payload: false });
  }, []);

  const login = async (credentials: LoginCredentials, signal?: AbortSignal) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const result = await AuthService.login(credentials, signal);

      if (!result.success) {
        dispatch({ type: 'LOGIN_FAILURE' });
        return { success: false, error: result.error };
      }

      // 2FA challenge — don't dispatch LOGIN_SUCCESS yet
      if (result.requires_2fa) {
        dispatch({ type: 'LOGIN_FAILURE' }); // reset isLoading
        return { success: true, requires_2fa: true, temp_token: result.temp_token };
      }

      dispatch({ type: 'LOGIN_SUCCESS', payload: { user: result.data.user, token: result.data.token } });
      return { success: true };
    } catch (err) {
      if (signal?.aborted) {
        dispatch({ type: 'LOGIN_FAILURE' });
        throw new DOMException('Login cancelled', 'AbortError');
      }
      dispatch({ type: 'LOGIN_FAILURE' });
      return { success: false, error: err instanceof Error ? err.message : 'An unexpected error occurred' };
    }
  };

  const verifyTwoFactor = async (temp_token: string, code: string) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const response = await AuthService.verifyTwoFactor(temp_token, code);
      if (response.success && response.data) {
        dispatch({ type: 'LOGIN_SUCCESS', payload: { user: response.data.user, token: response.data.token } });
        return { success: true };
      }
      dispatch({ type: 'LOGIN_FAILURE' });
      return { success: false, error: response.error?.message ?? 'Invalid code' };
    } catch (err) {
      dispatch({ type: 'LOGIN_FAILURE' });
      return { success: false, error: err instanceof Error ? err.message : 'An unexpected error occurred' };
    }
  };

  const logout = async () => {
    try { await AuthService.logout(); } finally { dispatch({ type: 'LOGOUT' }); }
  };

  const refreshUser = async () => {
    try {
      const response = await AuthService.getCurrentUser();
      if (response.success && response.data) {
        // Persist to localStorage so the updated totp_enabled survives a page refresh
        AuthService.setUser(response.data);
        dispatch({ type: 'SET_USER', payload: response.data });
      }
    } catch { /* non-critical */ }
  };

  return (
    <AuthContext.Provider value={{ ...state, login, verifyTwoFactor, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
